import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Row,
  Select,
  Switch,
  Table,
  Typography,
} from 'antd';
import { PlusOutlined } from '@ant-design/icons';
// Api
import {
  GET_TICKET_EVENT_SALES_REPORT,
  GET_TICKET_PRESALE_CODES,
} from 'api/ticketing/queries';
// Types
import {
  GetTicketPresaleCodes,
  GetTicketPresaleCodesVariables,
  GetTicketPresaleCodes_ticketPresaleCodes,
} from 'api/ticketing/types/GetTicketPresaleCodes';
import {
  GetTicketEventSalesReport,
  GetTicketEventSalesReportVariables,
} from 'api/ticketing/types/GetTicketEventSalesReport';
import { TicketEventDetailsFields } from 'api/ticketing/types/TicketEventDetailsFields';
import { TicketTierFields } from 'api/ticketing/types/TicketTierFields';
import { TicketEventStatus } from 'api/graphql-global-types';
// Hooks
import {
  CreatePresaleFormValues,
  usePresaleCodeActions,
} from './hooks/usePresaleCodeActions';
// Helpers
import {
  formatEventDateTime,
  validateForm,
  venueDateTimeRangePickerProps,
} from 'pages/Events/helpers';
// Constants
// UI
import PageToolbar from 'ui/PageToolbar/PageToolbar';
// Components
import LoadErrorAlert from 'pages/Events/components/LoadErrorAlert/LoadErrorAlert';
// Styles
import styles from './PresaleCodesTab.module.scss';

const { Paragraph, Text } = Typography;

type PresaleCode = GetTicketPresaleCodes_ticketPresaleCodes;

export const PRESALE_CODE_PATTERN = /^[A-Za-z0-9_-]{3,32}$/;

type PresaleCodesTabProps = {
  event: TicketEventDetailsFields;
  tiers: TicketTierFields[];
};

const PresaleCodesTab = ({
  event,
  tiers,
}: PresaleCodesTabProps): JSX.Element => {
  const [form] = Form.useForm<CreatePresaleFormValues>();
  const [createOpen, setCreateOpen] = useState(false);
  const { data, loading, error, refetch } = useQuery<
    GetTicketPresaleCodes,
    GetTicketPresaleCodesVariables
  >(GET_TICKET_PRESALE_CODES, {
    variables: { input: { ticketEventId: event.id } },
    fetchPolicy: 'cache-and-network',
  });
  const { data: reportData } = useQuery<
    GetTicketEventSalesReport,
    GetTicketEventSalesReportVariables
  >(GET_TICKET_EVENT_SALES_REPORT, {
    variables: { input: { ticketEventId: event.id } },
    fetchPolicy: 'cache-first',
  });
  const { createCode, setCodeActive, creating } = usePresaleCodeActions(event);

  const usage = new Map(
    (reportData?.ticketEventSalesReport.byPresaleCode || []).map((row) => [
      row.codeId,
      row,
    ])
  );
  const tierLabels = new Map(tiers.map((tier) => [tier.id, tier.label]));
  const cancelled = event.status === TicketEventStatus.Cancelled;

  const handleCreate = async (): Promise<void> => {
    const values = await validateForm(form);

    if (!values) {
      return;
    }

    const created = await createCode(values);

    if (created) {
      form.resetFields();
      setCreateOpen(false);
      refetch();
    }
  };

  const toggle = async (code: PresaleCode, active: boolean): Promise<void> => {
    const toggled = await setCodeActive(code, active);

    // A saved switch is already in the cache; a failed one may be out of date
    if (!toggled) {
      refetch();
    }
  };

  return (
    <>
      <Paragraph type="secondary">
        A presale code lets buyers in before or alongside general sale, while
        its window is open. Codes are case-insensitive.
      </Paragraph>
      <PageToolbar>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          disabled={cancelled}
          onClick={() => setCreateOpen(true)}
        >
          Create presale code
        </Button>
      </PageToolbar>

      {error ? (
        <LoadErrorAlert
          message="Failed to load the presale codes"
          error={error}
        />
      ) : (
        <Table<PresaleCode>
          rowKey="id"
          loading={loading && !data}
          dataSource={data?.ticketPresaleCodes || []}
          pagination={false}
          scroll={{ x: 900 }}
          locale={{ emptyText: 'No presale codes yet.' }}
          columns={[
            {
              title: 'Code',
              key: 'code',
              render: (_, code) => (
                <Text code copyable>
                  {code.code}
                </Text>
              ),
            },
            { title: 'Label', dataIndex: 'label', key: 'label' },
            {
              title: 'Tier',
              key: 'tier',
              render: (_, code) =>
                code.tierId ? tierLabels.get(code.tierId) || '—' : 'Any tier',
            },
            {
              title: 'Window',
              key: 'window',
              render: (_, code) => (
                <div>
                  <div>
                    {formatEventDateTime(code.startsAt, event.timezone)}
                  </div>
                  <Text type="secondary">
                    to {formatEventDateTime(code.endsAt, event.timezone)}
                  </Text>
                </div>
              ),
            },
            {
              title: 'Used',
              key: 'used',
              render: (_, code) => {
                const row = usage.get(code.id);
                const orders = row?.orders ?? 0;

                return code.maxUses ? `${orders} / ${code.maxUses}` : orders;
              },
            },
            {
              title: 'Tickets sold',
              key: 'tickets',
              render: (_, code) => usage.get(code.id)?.ticketsSold ?? 0,
            },
            {
              title: 'Active',
              key: 'active',
              render: (_, code) => (
                <Switch
                  checked={code.active}
                  aria-label={`${code.code} active`}
                  onChange={(checked) => toggle(code, checked)}
                />
              ),
            },
          ]}
        />
      )}

      <Modal
        title="Create presale code"
        visible={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={handleCreate}
        okText="Create code"
        confirmLoading={creating}
        destroyOnClose
      >
        <Form<CreatePresaleFormValues>
          form={form}
          layout="vertical"
          preserve={false}
        >
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="code"
                label="Code"
                normalize={(value?: string) => value?.toUpperCase()}
                rules={[
                  { required: true, message: 'Enter the code' },
                  {
                    pattern: PRESALE_CODE_PATTERN,
                    message: '3–32 letters, numbers, - or _',
                  },
                ]}
              >
                <Input placeholder="FANCLUB" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="label"
                label="Label"
                rules={[
                  { required: true, whitespace: true, message: 'Add a label' },
                  { max: 120, message: 'At most 120 characters' },
                ]}
              >
                <Input placeholder="Fan club presale" />
              </Form.Item>
            </Col>
          </Row>
          <Form.Item
            name="window"
            label={`Valid (${event.timezone})`}
            rules={[{ required: true, message: 'Set when the code works' }]}
          >
            <DatePicker.RangePicker
              {...venueDateTimeRangePickerProps(event.timezone)}
              className={styles.fullWidth}
            />
          </Form.Item>
          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="tierId" label="Tier">
                <Select
                  allowClear
                  placeholder="Any tier"
                  options={tiers.map((tier) => ({
                    value: tier.id,
                    label: tier.label,
                  }))}
                />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="maxUses"
                label="Max uses"
                extra="Counts checkouts, not tickets. Empty for unlimited."
                rules={[
                  {
                    type: 'integer',
                    min: 1,
                    max: 1000000,
                    message: 'From 1 to 1,000,000',
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  precision={0}
                  className={styles.fullWidth}
                />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default PresaleCodesTab;
